import { useToast } from '@airship-platform/airship-patterns';
import { Product, useManageProducts } from '@airship-platform/airship-proposal';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import { useDurationBilling } from '../../../data-offline/features/membershipData/useDurationBilling';
import { QuickAddButton } from '../../../patterns';

interface QuickAddEquipmentButtonProps {
  product: Product;
}

const QuickAddEquipmentButton: React.FC<QuickAddEquipmentButtonProps> = ({
  product,
}) => {
  const {
    proposalProducts,
    isLoadingProductIds,
    createAppointmentProposalOptionProduct,
    getNextSortOrder,
  } = useAppointmentProposalOptionProducts();
  const { currentOption } = useAppointmentProposalOptions();
  const { productDefaults } = useManageProducts();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const durationBillingItems = useDurationBilling(product.pricebookId);

  const isAlreadyAdded = useMemo(
    () =>
      !!currentOption?.appointmentProposalOptionId &&
      proposalProducts.some(
        (pp) =>
          pp.appointmentProposalOptionId ===
            currentOption.appointmentProposalOptionId &&
          pp.pricebookId === product.pricebookId
      ),
    [
      proposalProducts,
      currentOption?.appointmentProposalOptionId,
      product.pricebookId,
    ]
  );

  if (!currentOption) {
    return null;
  }

  const onClick = () => {
    if (durationBillingItems.length > 0) {
      navigate(product.pricebookId);
      return;
    }

    const defaultPrice = product.prices.find(
      (p) => p.priceType === product.defaultPriceType
    );

    return createAppointmentProposalOptionProduct(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      currentOption.appointmentProposalOptionId,
      {
        ...product,
        ...productDefaults,
        pricebookPrices: product.prices,
        jobId: currentOption.jobId,
        currency: 'USD',
        quantity: 1,
        price: defaultPrice?.value || 0,
        taxable: defaultPrice?.taxable || false,
        linkedItems: product.linkedItems.filter(
          (item) =>
            item.priceType ===
            (product.defaultPriceType === 'DYNAMIC'
              ? 'STANDARD'
              : product.defaultPriceType)
        ),
        sortOrder: getNextSortOrder(),
      }
    ).then(() =>
      showToast(`Added ${product.name} to ${currentOption.name}`, 'success')
    );
  };

  return (
    <QuickAddButton
      isLoading={isLoadingProductIds.includes(product.pricebookId)}
      isAlreadyAdded={isAlreadyAdded}
      disabled={!currentOption || isAlreadyAdded || currentOption.isReadOnly}
      onClick={onClick}
    />
  );
};

export default QuickAddEquipmentButton;
